import React, { ChangeEvent } from "react";
import {
  Box,
  FormHelperText,
  FormLabel,
  Grid,
  Radio as MuiRadio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { styled } from "@mui/material/styles";

type Option = {
  value: any;
  label: string | JSX.Element;
  // CS-1666 - Optional sub-label for card-style radio design
  subLabel?: string;
  leftIcon?: JSX.Element;
  disabled?: boolean;
};

interface RadioButtonProps {
  name?: string;
  options?: Option[];
  label?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  control: Control<any, any>;
  isDisabled?: boolean;
  rules?: object;
  controllerName?: string;
  xs?: number;
  md?: number;
  value?: string;
  defaultValue?: string | boolean;
  leftIcon?: JSX.Element;
  isDashboard?: boolean;
}

const StyledOption = styled(Box)<{ selected: boolean }>(({ selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px",
  borderRadius: "8px",
  fontWeight: "bold",
  fontSize: "16px",
  color: selected ? "white" : "rgba(255, 255, 255, 0.7)",
  backgroundColor: selected ? "#2D5939" : "transparent",
  border: selected ? "none" : "1px solid rgba(255, 255, 255, 0.5)",
  cursor: "pointer",
  transition: "all 0.3s ease",
}));

const StyledRadio = styled(MuiRadio)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "white" : "#0f2d16",
  "&.Mui-checked": {
    color: "#0f2d16",
  },
}));

const CustomStyledRadio: React.FC<RadioButtonProps> = (props) => {
  const {
    name,
    options,
    label,
    onChange,
    control,
    isDisabled,
    rules,
    controllerName,
    defaultValue,
    leftIcon,
    isDashboard = false,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const accent = "#14b8a6";
  const accentBg = isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.10)";
  const accentBorder = "rgba(20,184,166,0.55)";
  const idleBorder = isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.2)";
  const idleHoverBg = isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.04)";
  const textPrimary = isDark ? "#f1f5f9" : "#0f172a";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";

  return (
    <Controller
      name={controllerName || ""}
      control={control}
      rules={rules || {}}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => (
        <>
          {label && (
            <FormLabel
              component="legend"
              sx={{
                ...(isDashboard
                  ? { fontSize: "0.8rem", fontWeight: 600, color: fieldState.error ? theme.palette.error.main : textSecondary, mb: 1 }
                  : { color: fieldState.error ? theme.palette.error.main : "inherit", marginBottom: "8px" }
                ),
              }}
            >
              {label}
            </FormLabel>
          )}

          <RadioGroup
            defaultValue={defaultValue}
            name={name || controllerName}
            // CS-1666 - Always controlled so RadioGroup onChange fires and parent callback (e.g. setSharingMode) is invoked
            value={field.value ?? ""}
            onChange={(e) => {
              field.onChange(e.target.value);
              onChange?.(e);
            }}
          >
            <Grid container spacing={isDashboard ? 1.5 : 2}>
              {options?.map((option) => {
                const Left = option.leftIcon ?? leftIcon;
                const isSelected = field.value === option.value;
                const isOptionDisabled = isDisabled || option.disabled;

                if (isDashboard) {
                  return (
                    <Grid item xs={6} key={option.value}>
                      <Box
                        component="label"
                        htmlFor={`${controllerName}-${option.value}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                          px: 1.6,
                          py: 1.3,
                          borderRadius: "10px",
                          cursor: isOptionDisabled ? "not-allowed" : "pointer",
                          border: `1.5px solid ${isSelected ? accentBorder : idleBorder}`,
                          backgroundColor: isSelected ? accentBg : "transparent",
                          opacity: isOptionDisabled && !isSelected ? 0.38 : 1,
                          pointerEvents: isOptionDisabled && !isSelected ? "none" : "auto",
                          transition: "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
                          boxShadow: isSelected ? `0 0 0 3px rgba(20,184,166,0.12)` : "none",
                          "&:hover": !isOptionDisabled && !isSelected
                            ? { backgroundColor: idleHoverBg, borderColor: isDark ? "rgba(148,163,184,0.35)" : "rgba(100,116,139,0.35)" }
                            : {},
                          userSelect: "none",
                        }}
                      >
                        <Box sx={{
                          width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                          border: `2px solid ${isSelected ? accent : (isDark ? "rgba(148,163,184,0.4)" : "rgba(100,116,139,0.4)")}`,
                          backgroundColor: isSelected ? accent : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.18s ease",
                        }}>
                          {isSelected && (
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#fff" }} />
                          )}
                        </Box>

                        <input
                          id={`${controllerName}-${option.value}`}
                          type="radio"
                          value={option.value}
                          checked={isSelected}
                          onChange={() => field.onChange(option.value)}
                          disabled={!!isOptionDisabled}
                          style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
                        />

                        {Left && !isMobile && (
                          <Box sx={{
                            display: "flex", alignItems: "center",
                            color: isSelected ? accent : textSecondary,
                            transition: "color 0.18s ease",
                            "& svg": { fontSize: "1.1rem" },
                          }}>
                            {Left}
                          </Box>
                        )}

                        <Box sx={{
                          flex: 1, fontSize: "0.85rem",
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? (isDark ? "#fff" : textPrimary) : textSecondary,
                          transition: "color 0.18s ease",
                          lineHeight: 1.3,
                        }}>
                          {option.label}
                        </Box>
                      </Box>
                    </Grid>
                  );
                }

                // CS-1666 - Card style: when any option has a subLabel, render card design with main label + sub-label
                const hasSubLabels = options?.some((o) => o.subLabel);
                if (hasSubLabels) {
                  const selectedGreenBg = isDark ? "rgba(45,89,57,0.45)" : "rgba(220,252,231,0.7)";
                  const selectedGreenBorder = isDark ? "#4ade80" : "#16a34a";
                  const cardBorder = isSelected
                    ? selectedGreenBorder
                    : isDark ? "rgba(148,163,184,0.22)" : "rgba(100,116,139,0.22)";
                  const cardBg = isSelected
                    ? selectedGreenBg
                    : isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
                  const radioColor = isSelected
                    ? (isDark ? "#4ade80" : "#16a34a")
                    : (isDark ? "rgba(148,163,184,0.5)" : "rgba(100,116,139,0.4)");

                  return (
                    <Grid item xs={6} key={option.value}>
                      <Box
                        component="label"
                        htmlFor={`${controllerName}-${option.value}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          width: "100%",
                          px: 2,
                          py: 1.5,
                          borderRadius: "10px",
                          cursor: isOptionDisabled ? "not-allowed" : "pointer",
                          border: `1.5px solid ${cardBorder}`,
                          backgroundColor: cardBg,
                          opacity: isOptionDisabled && !isSelected ? 0.4 : 1,
                          pointerEvents: isOptionDisabled && !isSelected ? "none" : "auto",
                          transition: "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
                          boxShadow: isSelected
                            ? `0 0 0 3px ${isDark ? "rgba(74,222,128,0.12)" : "rgba(22,163,74,0.10)"}`
                            : "none",
                          "&:hover": !isOptionDisabled && !isSelected
                            ? {
                                borderColor: isDark ? "rgba(148,163,184,0.4)" : "rgba(100,116,139,0.35)",
                                backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                              }
                            : {},
                          userSelect: "none",
                        }}
                      >
                        {/* Custom radio dot */}
                        <Box
                          sx={{
                            mt: "2px",
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            flexShrink: 0,
                            border: `2px solid ${radioColor}`,
                            backgroundColor: isSelected ? radioColor : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.18s ease",
                          }}
                        >
                          {isSelected && (
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#fff" }} />
                          )}
                        </Box>

                        <input
                          id={`${controllerName}-${option.value}`}
                          type="radio"
                          name={name || controllerName}
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => {
                            field.onChange(option.value);
                            onChange?.(e as any);
                          }}
                          disabled={!!isOptionDisabled}
                          style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
                        />

                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isSelected
                                ? (isDark ? "#f0fdf4" : "#14532d")
                                : textPrimary,
                              lineHeight: 1.4,
                              transition: "color 0.18s ease",
                            }}
                          >
                            {option.label}
                          </Typography>
                          {option.subLabel && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isSelected
                                  ? (isDark ? "#86efac" : "#166534")
                                  : textSecondary,
                                lineHeight: 1.3,
                                display: "block",
                                mt: 0.25,
                                transition: "color 0.18s ease",
                              }}
                            >
                              {option.subLabel}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={6} key={option.value}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        cursor: "pointer",
                        border: isSelected ? "1px solid green" : "1px solid gray",
                        padding: "12px",
                        borderRadius: "8px",
                        justifyContent: "space-between",
                        backgroundColor: isSelected
                          ? isDark ? "#2D5939" : "#4c9c62"
                          : "transparent",
                        fontWeight: "bold",
                        transition: "all 0.3s ease",
                        opacity: isOptionDisabled && !isSelected ? 0.4 : 1,
                        pointerEvents: isOptionDisabled && !isSelected ? "none" : "auto",
                      }}
                    >
                      {Left && !isMobile && <Box>{Left}</Box>}
                      {option.label}
                      <StyledRadio
                        value={option.value}
                        checked={isSelected}
                        onChange={() => field.onChange(option.value)}
                        disabled={!!isOptionDisabled}
                      />
                    </label>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>

          {fieldState.error && (
            <FormHelperText error sx={isDashboard ? { mt: 0.8, fontSize: "0.78rem" } : undefined}>
              {fieldState.error.message}
            </FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default CustomStyledRadio;